import { useCallback, useEffect, useState } from "react";
import {
  BookUser,
  Boxes,
  Cloud,
  CloudOff,
  LayoutDashboard,
  ListFilter,
  LogOut,
  MessageSquareText,
  ShieldCheck,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { AdminWorkspace } from "@/features/admin/AdminWorkspace";
import { AgentFloating } from "@/features/agent/AgentFloating";
import { AgentWorkspace } from "@/features/agent/AgentWorkspace";
import { AuthScreen } from "@/features/auth/AuthScreen";
import { PasswordResetScreen } from "@/features/auth/PasswordResetScreen";
import { ContactsView } from "@/features/contacts/ContactsView";
import { Dashboard } from "@/features/dashboard/Dashboard";
import { LeadForm } from "@/features/leads/LeadForm";
import { LeadEditExtras } from "@/features/leads/LeadEditExtras";
import { LeadsView } from "@/features/leads/LeadsView";
import { useLeads } from "@/features/leads/useLeads";
import { createEmptyLead } from "@/features/leads/utils";
import type { Account, Lead } from "@/features/leads/types";

type View = "dashboard" | "leads" | "contacts" | "admin";

const navigation = [
  { id: "dashboard" as const, label: "交易看板", icon: LayoutDashboard },
  { id: "leads" as const, label: "交易线索", icon: ListFilter },
  { id: "contacts" as const, label: "联系人", icon: BookUser },
];

function App() {
  const [account, setAccount] = useState<Account | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [recoveryMode, setRecoveryMode] = useState(
    () => window.location.pathname === "/reset-password",
  );
  const [view, setView] = useState<View>("dashboard");
  const [agentOpen, setAgentOpen] = useState(false);
  const [formLead, setFormLead] = useState<Lead | null>(null);
  const [formMode, setFormMode] = useState<"new" | "edit">("new");
  const [saving, setSaving] = useState(false);
  const [directory, setDirectory] = useState<
    { id: string; username: string }[]
  >([]);
  const [contactFilter, setContactFilter] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const handleSessionRevoked = useCallback((reason: "kick" | "ban") => {
    setAccount(null);
    setAgentOpen(false);
    setFormLead(null);
    toast.error(reason === "ban" ? "账号已被管理员封禁" : "会话已被管理员终止");
  }, []);
  const { leads, loading, syncState, saveLead, deleteLead, applyLeadChanges } =
    useLeads(Boolean(account), handleSessionRevoked);
  const visibleNavigation =
    account?.role === "admin"
      ? [
          ...navigation,
          { id: "admin" as const, label: "管理员", icon: ShieldCheck },
        ]
      : navigation;

  useEffect(() => {
    let active = true;
    void api<{ account: Account | null }>("/api/auth/session")
      .then(({ account: current }) => {
        if (active) setAccount(current);
      })
      .catch(() => {
        if (active) setAccount(null);
      })
      .finally(() => {
        if (active) setAuthLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!account) {
      setDirectory([]);
      return;
    }
    let active = true;
    void api<{ users: { id: string; username: string }[] }>("/api/directory")
      .then(({ users }) => {
        if (active) setDirectory(users);
      })
      .catch(() => {
        if (active) setDirectory([]);
      });
    return () => {
      active = false;
    };
  }, [account]);

  useEffect(() => {
    if (view === "admin" && account?.role !== "admin") setView("dashboard");
  }, [account?.role, view]);

  useEffect(() => {
    const handleUnauthorized = () => {
      if (account) handleSessionRevoked("kick");
    };
    window.addEventListener("huopan:unauthorized", handleUnauthorized);
    return () =>
      window.removeEventListener("huopan:unauthorized", handleUnauthorized);
  }, [account, handleSessionRevoked]);

  const openNew = () => {
    setFormMode("new");
    setFormLead(createEmptyLead("sell", account?.email ?? "我"));
  };
  const openEdit = (id: string) => {
    const lead = leads.find((item) => item.id === id);
    if (!lead) return;
    setFormMode("edit");
    setFormLead(lead);
  };

  const submitForm = async (lead: Lead) => {
    setSaving(true);
    try {
      await saveLead(lead);
      setFormLead(null);
      toast.success(formMode === "new" ? "盘口已创建" : "盘口已更新");
    } catch (reason) {
      toast.error(reason instanceof Error ? reason.message : "保存失败");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteLead(id);
      setFormLead(null);
      toast.success("盘口已删除");
    } catch (reason) {
      toast.error(reason instanceof Error ? reason.message : "删除失败");
    }
  };

  const logout = async () => {
    try {
      await api<{ ok: true }>("/api/auth/logout", { method: "POST" });
      setAccount(null);
    } catch (reason) {
      toast.error(reason instanceof Error ? reason.message : "退出失败");
    }
  };

  const syncCopy = {
    connecting: "正在连接云端",
    online: "云端已同步",
    offline: "云端连接异常",
  }[syncState];

  if (authLoading)
    return (
      <div className="grid min-h-dvh place-items-center bg-background">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <Boxes className="size-5 animate-pulse text-primary" />
          正在恢复账号会话…
        </div>
      </div>
    );
  if (recoveryMode)
    return (
      <PasswordResetScreen
        onComplete={(recoveredAccount) => {
          setAccount(recoveredAccount);
          setRecoveryMode(false);
        }}
      />
    );
  if (!account)
    return (
      <>
        <AuthScreen onLogin={setAccount} />
        <Toaster theme="dark" position="top-center" />
      </>
    );

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-52 flex-col border-r bg-sidebar md:flex">
        <button
          type="button"
          className="flex h-12 shrink-0 items-center gap-2.5 border-b px-4 text-sm font-semibold"
          onClick={() => setView("dashboard")}
          aria-label="返回交易看板"
          title="货盘 Lite"
        >
          <Boxes className="size-[18px]" />
          <span>货盘 Lite</span>
        </button>
        <nav aria-label="主导航" className="flex w-full flex-col gap-1 p-2">
          {visibleNavigation.map((item) => (
            <button
              key={item.id}
              type="button"
              title={item.label}
              aria-label={item.label}
              aria-current={view === item.id ? "page" : undefined}
              onClick={() => setView(item.id)}
              className={`flex h-9 items-center gap-2.5 rounded px-3 text-sm transition ${view === item.id ? "bg-accent font-medium text-accent-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
            >
              <item.icon className="size-4" />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="mt-auto border-t p-3">
          <div
            className="flex min-w-0 items-center gap-2.5 px-1 py-1"
            title={account.email}
          >
            <span
              className="grid size-7 shrink-0 place-items-center rounded bg-accent text-[11px] font-medium"
              aria-hidden="true"
            >
              {account.email.trim().charAt(0).toUpperCase()}
            </span>
            <span className="truncate text-xs text-muted-foreground">
              {account.email}
            </span>
          </div>
        </div>
      </aside>

      <div className="md:pl-52">
        <header className="sticky top-0 z-30 border-b bg-background/90 backdrop-blur-xl">
          <div className="flex h-12 items-center justify-between gap-4 px-4 sm:px-5">
            <button
              type="button"
              className="flex min-w-0 items-center gap-2 text-sm font-medium"
              onClick={() => setView("dashboard")}
              aria-label="返回交易看板"
            >
              <Boxes className="size-4 md:hidden" />
              <span>
                {view === "dashboard"
                  ? "交易看板"
                  : view === "leads"
                    ? "交易线索"
                    : view === "contacts"
                      ? "联系人"
                      : "管理员工作台"}
              </span>
            </button>
            <div className="flex items-center gap-1">
              <span
                role="status"
                aria-label={syncCopy}
                title={syncCopy}
                className={`grid size-8 place-items-center ${syncState === "offline" ? "text-destructive" : "text-muted-foreground"}`}
              >
                {syncState === "offline" ? (
                  <CloudOff className="size-4" />
                ) : (
                  <Cloud
                    className={`size-4 ${syncState === "online" ? "text-emerald-300" : ""}`}
                  />
                )}
              </span>
              <Button
                variant={agentOpen ? "secondary" : "ghost"}
                size="icon-sm"
                onClick={() => setAgentOpen((open) => !open)}
                aria-expanded={agentOpen}
                aria-label="Agent"
                title="Agent"
              >
                <MessageSquareText />
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => void logout()}
                aria-label="退出登录"
                title="退出登录"
              >
                <LogOut />
              </Button>
            </div>
          </div>
          <nav
            aria-label="移动端主导航"
            className="grid border-t md:hidden"
            style={{
              gridTemplateColumns: `repeat(${visibleNavigation.length}, minmax(0, 1fr))`,
            }}
          >
            {visibleNavigation.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setView(item.id)}
                aria-label={item.label}
                title={item.label}
                aria-current={view === item.id ? "page" : undefined}
                className={`flex min-h-11 items-center justify-center ${view === item.id ? "bg-accent text-foreground" : "text-muted-foreground"}`}
              >
                <item.icon className="size-4" />
                <span className="sr-only">{item.label}</span>
              </button>
            ))}
          </nav>
        </header>

        <main className="mx-auto max-w-[1536px] px-4 py-4 sm:px-5 lg:px-6">
          {view === "dashboard" && (
            <Dashboard
              leads={leads}
              onSelect={openEdit}
              onAgent={() => setAgentOpen(true)}
              users={directory}
              contactFilter={contactFilter}
              onClearContactFilter={() => setContactFilter(null)}
            />
          )}
          {view === "leads" && (
            <LeadsView
              leads={leads}
              loading={loading}
              onSelect={openEdit}
              onNew={openNew}
              onAgent={() => setAgentOpen(true)}
              contactFilter={contactFilter}
              onClearContactFilter={() => setContactFilter(null)}
            />
          )}
          {view === "contacts" && (
            <ContactsView
              leads={leads}
              onViewLeads={(contact) => {
                setContactFilter({ id: contact.id, name: contact.name });
                setView("dashboard");
              }}
            />
          )}
          {view === "admin" && account.role === "admin" && (
            <AdminWorkspace currentAccountId={account.id} />
          )}
        </main>
      </div>

      <Dialog
        open={Boolean(formLead)}
        onOpenChange={(open) => {
          if (!open) setFormLead(null);
        }}
      >
        <DialogContent className="flex max-h-[90dvh] max-w-[calc(100%-1.5rem)] flex-col sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {formMode === "new" ? "手工新增盘口" : "编辑盘口"}
            </DialogTitle>
            <DialogDescription>
              带 * 为必填，价格留空表示面议。
            </DialogDescription>
          </DialogHeader>
          {formLead && (
            <LeadForm
              initial={formLead}
              onSubmit={submitForm}
              onCancel={() => setFormLead(null)}
              onDelete={
                formMode === "edit"
                  ? () => handleDelete(formLead.id)
                  : undefined
              }
              busy={saving}
              submitLabel={formMode === "new" ? "创建盘口" : "保存修改"}
              users={directory}
            >
              {formMode === "edit" && (
                <LeadEditExtras
                  lead={formLead}
                  leads={leads}
                  directory={directory}
                  currentUserId={account.id}
                  onViewContact={() => {
                    if (!formLead.contact_id) return;
                    setContactFilter({
                      id: formLead.contact_id,
                      name: formLead.contact_name || "货主",
                    });
                    setFormLead(null);
                    setView("dashboard");
                  }}
                />
              )}
            </LeadForm>
          )}
        </DialogContent>
      </Dialog>
      <AgentFloating
        open={agentOpen}
        onClose={() => setAgentOpen(false)}
        owner={account.id ?? account.email}
      >
        {(dragHandleProps) => (
          <AgentWorkspace
            leads={leads}
            owner={account.id ?? account.email}
            apply={applyLeadChanges}
            onClose={() => setAgentOpen(false)}
            dragHandleProps={dragHandleProps}
          />
        )}
      </AgentFloating>
      <Toaster theme="dark" position="top-center" richColors />
    </div>
  );
}

export default App;
